/**
 * Home-page "Departed this year" strip — a slim horizontal row of the most
 * recently deceased notable people. Wikipedia-sourced, respectful tone.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import { Flower, ArrowRight } from "lucide-react";

export default function InMemoriamStrip({ limit = 8 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    http.get("/in-memoriam/recent", { params: { limit } })
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [limit]);

  if (!loading && items.length === 0) return null;

  return (
    <section
      data-testid="in-memoriam-strip"
      className="w-full border-y"
      style={{ background: "var(--wofy-surface)", borderColor: "var(--wofy-border)" }}
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Flower className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
              <div className="overline" data-testid="in-memoriam-strip-title">DEPARTED THIS YEAR</div>
            </div>
            <h2 className="font-display text-3xl md:text-4xl leading-tight">
              Remembering those we've lost.
            </h2>
            <p className="mt-2 max-w-2xl text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              A respectful tribute to notable people who have passed away recently.
            </p>
          </div>
          <Link
            to="/in-memoriam"
            data-testid="in-memoriam-strip-see-all"
            className="wofy-btn-ghost text-sm"
          >
            See all tributes <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {loading ? (
          <div className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>Loading…</div>
        ) : (
          <div className="flex gap-4 overflow-x-auto pb-2" style={{ scrollbarWidth: "thin" }}>
            {items.map((p) => (
              <Link
                key={p.id}
                to={`/world-changers/${p.slug || p.id}`}
                data-testid={`in-memoriam-strip-item-${p.slug || p.id}`}
                className="shrink-0 w-56 rounded-2xl overflow-hidden border hover:shadow-lg transition-shadow"
                style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
              >
                <div
                  className="w-full h-32 bg-cover bg-center"
                  style={{
                    backgroundImage: `url(${p.photo_url || "/wofy-og-fallback.png"})`,
                    backgroundColor: "var(--wofy-surface)",
                  }}
                />
                <div className="p-3">
                  <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: "var(--wofy-primary)" }}>
                    {[p.born, p.died].filter(Boolean).join(" – ")}
                  </div>
                  <div className="mt-1 font-display text-base leading-tight line-clamp-2">
                    {p.name}
                  </div>
                  {p.occupation && (
                    <div className="mt-1 text-xs line-clamp-1" style={{ color: "var(--wofy-text-soft)" }}>
                      {p.occupation}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
